import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '../config';
import { BackendResponse, SaveDepartment } from '../../types/interfaces';
import { deserializeResponse, replaceUrlParams } from '../utils/helpers';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { Product } from '../../../shared/models/product.model';
import {
  Department,
  DepartmentWithResidents,
} from '../../../shared/models/department.model';
import { departmentIcons } from '../../../shared/utils/departmentIcons';
import { Resident } from '../../../shared/models/resident.model';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '../utils/snackbar.service';
import { sortResidents } from '../../../shared/utils/utils';
import { DepartmentTypeEnum } from '../../types/enums';
import { AuthenticationService } from '../authentication';
import { TestCustomerService } from '../test-customer/test-customer.service';
import { ResponseStatus } from '../../types/enums';

@Injectable({
  providedIn: 'root',
})
export class DepartmentsService {
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    private testCustomerService: TestCustomerService
  ) {}

  private createNewDepartment(departmentName: string): Department {
    const newDepartment = {
      color: '',
      iconUrl: '',
      department: departmentName,
      departmentId: 0,
      name: departmentName,
      customerId: 324,
      people: [],
      isChecked: false,
      peopleCount: 0,
      id: 0,
    };
    return new DepartmentWithResidents(newDepartment);
  }

  // Residents are managed separately and must not be sent when saving a
  // department, so they're stripped from the outgoing payload here.
  private mapDepartmentToSave(department: SaveDepartment) {
    const { people, ...departmentToSave } = department;
    return departmentToSave;
  }

  saveDepartment(
    department: SaveDepartment
  ): Observable<BackendResponse<{ id?: number }>> {
    if (this.testCustomerService.isLoggedInAsTestCustomer()) {
      const departmentId = this.testCustomerService.saveDepartment(
        department,
        true
      );
      return of({
        status: ResponseStatus.Success,
        message: 'Department saved successfully for test customer',
        data: { id: departmentId },
      });
    }

    const url = this.configService.endpoints.departments.departments;
    return this.http
      .post<
        BackendResponse<{ id?: number }>
      >(url, this.mapDepartmentToSave(department))
      .pipe(
        map(res => res),
        catchError(error => {
          if (error.isHandled) return;
          this.snackbarService.error(
            this.translateService.instant(
              'departments.error.errorInDepartmentSaving'
            )
          );
          return throwError(() => error);
        })
      );
  }

  deleteDepartment(departmentId: number): Observable<BackendResponse> {
    if (this.testCustomerService.isLoggedInAsTestCustomer()) {
      this.testCustomerService.removeDepartment(departmentId);
      return of({
        status: ResponseStatus.Success,
        message: 'Department deleted successfully for test customer',
      });
    }
    const url = replaceUrlParams(
      this.configService.endpoints.departments.departmentWithResidents,
      ['departmentId'],
      [departmentId]
    );
    return this.http.delete<BackendResponse>(url).pipe(
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant(
            'departments.error.errorInDepartmentDeleting'
          )
        );
        return throwError(() => error);
      })
    );
  }

  getDepartments(): Observable<BackendResponse<Department[]>> {
    const url = this.configService.endpoints.departments.departments;
    return this.http
      .get<BackendResponse<Department[]>>(url)
      .pipe(
        map((arg: any) => deserializeResponse<Department[]>(arg, Department))
      );
  }

  getDepartmentWithResidents(
    departmentId: number
  ): Observable<BackendResponse<DepartmentWithResidents>> {
    if (
      this.testCustomerService.isLoggedInAsTestCustomer() &&
      this.testCustomerService.hasDepartment(departmentId)
    ) {
      const stored = this.testCustomerService.getDepartment(departmentId);

      if (stored) {
        return of({
          data: stored,
          status: ResponseStatus.Success,
          message: 'Successfully retrieved department for test customer',
        } as BackendResponse<DepartmentWithResidents>);
      }

      return throwError(() => new Error('DEPARTMENT_NOT_FOUND'));
    }

    const url = replaceUrlParams(
      this.configService.endpoints.departments.departmentWithResidents,
      ['departmentId'],
      [departmentId]
    );

    return this.http.get<BackendResponse<DepartmentWithResidents>>(url).pipe(
      map((arg: any) =>
        deserializeResponse<DepartmentWithResidents>(
          arg,
          DepartmentWithResidents
        )
      ),
      catchError(error => {
        if (error.isHandled) {
          return throwError(() => error);
        }

        this.snackbarService.error(
          this.translateService.instant(
            'departments.error.errorInDepartmentRetrieving'
          )
        );

        return throwError(() => error);
      }),
      map(response => {
        response.data.people = response.data.people.sort((a, b) =>
          a.lname.localeCompare(b.lname)
        );
        return response;
      }),
      tap(department => {
        if (this.testCustomerService.isLoggedInAsTestCustomer()) {
          this.testCustomerService.saveDepartment(department.data, true);
        }
      })
    );
  }

  getDepartmentsWithResidents(): Observable<
    BackendResponse<DepartmentWithResidents[]>
  > {
    const url =
      this.configService.endpoints.departments.departmentsWithResidents;
    return this.http.get<BackendResponse<DepartmentWithResidents>>(url).pipe(
      map((arg: any) =>
        deserializeResponse<DepartmentWithResidents[]>(
          arg,
          DepartmentWithResidents
        )
      ),
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant(
            'departments.error.errorInDepartmentRetrieving'
          )
        );
        return throwError(() => error);
      }),
      map(response => {
        response.data.forEach(d => {
          d.people = d.people.sort((a, b) => a.lname.localeCompare(b.lname));
        });
        return response;
      })
    );
  }
}
